<template>
  <div class="user-info-head" @click="editCropper()">
    <img :src="options.img" :title="$t('headerWidgets.clickUploadAvatar')" class="img-circle img-lg" />
    <el-dialog v-model="open" :title="title" width="800px" class="customClass_pro" append-to-body @opened="modalOpened"
      @close="closeDialog">
      <el-row>
        <el-col :xs="24" :md="12" :style="{ height: '350px' }">
          <vue-cropper v-if="visible" ref="cropper" :img="options.img" :info="true" :auto-crop="options.autoCrop"
            :auto-crop-width="options.autoCropWidth" :auto-crop-height="options.autoCropHeight"
            :fixed-box="options.fixedBox" :output-type="options.outputType" @real-time="realTime" />
        </el-col>
        <el-col :xs="24" :md="12" :style="{ height: '350px' }">
          <div class="avatar-upload-preview">
            <img :src="options.previews.url" :style="options.previews.img" />
          </div>
        </el-col>
      </el-row>
      <br />
      <el-row>
        <el-col :lg="2" :md="2">
          <el-upload action="#" :http-request="requestUpload" :show-file-list="false" :before-upload="beforeUpload">
            <el-button>
              {{ $t('AlarmFence.chooseFile') }}
              <el-icon class="el-icon--right">
                <Upload />
              </el-icon>
            </el-button>
          </el-upload>
        </el-col>
        <el-col :lg="{ span: 1, offset: 2 }" :md="2">
          <el-button icon="Plus" @click="changeScale(1)"></el-button>
        </el-col>
        <el-col :lg="{ span: 1, offset: 1 }" :md="2">
          <el-button icon="Minus" @click="changeScale(-1)"></el-button>
        </el-col>
        <el-col :lg="{ span: 1, offset: 1 }" :md="2">
          <el-button icon="RefreshLeft" @click="rotateLeft()"></el-button>
        </el-col>
        <el-col :lg="{ span: 1, offset: 1 }" :md="2">
          <el-button icon="RefreshRight" @click="rotateRight()"></el-button>
        </el-col>
        <el-col :lg="{ span: 2, offset: 6 }" :md="2">
          <el-button type="primary" @click="uploadImg()">{{ $t('headerWidgets.submit') }}</el-button>
        </el-col>
      </el-row>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import 'vue-cropper/dist/index.css';
import { VueCropper } from 'vue-cropper';
import { uploadAvatar } from '@/api/system/user';
import useUserStore from '@/store/modules/user';
import { useI18n } from 'vue-i18n';
import { UploadRawFile } from 'element-plus';

// i18n 翻译函数：用于头像弹窗标题与上传提示文案的多语言展示
const { t } = useI18n();

interface Options {
  img: string | any; // 裁剪图片的地址
  autoCrop: boolean; // 是否默认生成截图框
  autoCropWidth: number; // 默认生成截图框宽度
  autoCropHeight: number; // 默认生成截图框高度
  fixedBox: boolean; // 固定截图框大小 不允许改变
  fileName: string;
  previews: any; // 预览数据
  outputType: string;
  visible: boolean;
}

const userStore = useUserStore();
const { proxy } = getCurrentInstance() as ComponentInternalInstance;

const open = ref(false);
const visible = ref(false);
// 头像编辑弹窗标题
const title = ref(t('headerWidgets.editAvatar'));

const cropper = ref<any>({});
//图片裁剪数据
const options = reactive<Options>({
  img: userStore.avatar,
  autoCrop: true,
  autoCropWidth: 200,
  autoCropHeight: 200,
  fixedBox: true,
  outputType: 'png',
  fileName: '',
  previews: {},
  visible: false
});

/** 编辑头像 */
const editCropper = () => {
  open.value = true;
};
/** 打开弹出层结束时的回调 */
const modalOpened = () => {
  visible.value = true;
};
/** 覆盖默认上传行为 */
const requestUpload = (): any => { };
/** 向左旋转 */
const rotateLeft = () => {
  cropper.value.rotateLeft();
};
/** 向右旋转 */
const rotateRight = () => {
  cropper.value.rotateRight();
};
/** 图片缩放 */
const changeScale = (num: number) => {
  num = num || 1;
  cropper.value.changeScale(num);
};
/** 上传预处理 */
const beforeUpload = (file: UploadRawFile): any => {
  if (file.type.indexOf('image/') == -1) {
    proxy?.$modal.msgError(t('headerWidgets.avatarFormatError'));
  } else {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      options.img = reader.result;
      options.fileName = file.name;
    };
  }
};
/** 上传图片 */
const uploadImg = async () => {
  cropper.value.getCropBlob(async (data: any) => {
    let formData = new FormData();
    // 未重新上传文件时 fileName 为空字符串，后端按扩展名做格式校验会直接拒绝；
    // 裁剪器 outputType 固定为 png，故用 avatar.png 兜底，保证基于回显图裁剪后直接提交可用。
    formData.append('avatarfile', data, options.fileName || 'avatar.png');
    const res = await uploadAvatar(formData);
    open.value = false;
    options.img = res.data.imgUrl;
    userStore.setAvatar(options.img);
    proxy?.$modal.msgSuccess(t('profileModal.updateSuccess'));
    visible.value = false;
  });
};
/** 实时预览 */
const realTime = (data: any) => {
  options.previews = data;
};
/** 关闭窗口 */
const closeDialog = () => {
  options.img = userStore.avatar;
  options.visible = false;
};
</script>

<style lang="scss" scoped>
.user-info-head {
  position: relative;
  display: inline-block;
  height: 120px;
}

.user-info-head:hover:after {
  content: '+';
  position: absolute;
  display: flex;
  justify-content: center;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  color: #eee;
  background: rgba(0, 0, 0, 0.5);
  font-size: 24px;
  font-style: normal;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  cursor: pointer;
  line-height: 110px;
  border-radius: 50%;
}
</style>
<style lang="scss">
.customClass_pro {
  border-radius: 10px;
  background-color: #1a1e24;

  .el-dialog__title {
    font-size: 18px;
    color: #fff;
    letter-spacing: 1px;
  }

  .el-dialog__headerbtn .el-dialog__close {
    color: #64748b;
    font-size: 20px !important;
  }

  .el-button--default {
    background-color: transparent;
    color: #0d83fc;
    border: 1px solid #0d83fc;
    letter-spacing: 1px;
  }

  .el-button--primary {
    background-color: #0d83fc;
    color: white;
    border: 1px solid #0d83fc;
    letter-spacing: 1px;
  }

  .dialog-footer {
    .el-button {
      width: 120px;
      margin: 0 20px;
    }

    .el-button--default {
      background-color: transparent;
      color: #0d83fc;
      border: 1px solid #0d83fc;
      letter-spacing: 1px;
    }

    .el-button--primary {
      background-color: #0d83fc;
      color: white;
      border: 1px solid #0d83fc;
      letter-spacing: 1px;
    }
  }
}
</style>
